import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { useAuth } from '../../../contexts/AuthContext';
import { ArrowLeft, Package } from 'lucide-react';

interface OrderDetails {
  id: string;
  orderNumber: string;
  sellerId: string;
  sellerName?: string;
  items: Array<{
    productId: string;
    name: string;
    quantity: number;
    price: number;
    total: number;
    mainImage?: string;
  }>;
  shippingMethod: string;
  shippingAddress: {
    name: string;
    email: string;
    phone: string;
    address: string;
    city: string;
    postalCode: string;
    country: string;
    district: string;
  };
  subtotal: number;
  shippingCost: number;
  total: number;
  status: string;
  createdAt: any;
  comments: Array<{
    id: string;
    text: string;
    createdAt: any;
    createdBy: string;
    userId: string;
  }>;
}

export default function OrderDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [order, setOrder] = useState<OrderDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  useEffect(() => {
    if (!currentUser) {
      navigate('/dashboard/login');
      return;
    }
    loadOrderDetails();
  }, [currentUser, id]);

  const loadOrderDetails = async () => {
    try {
      if (!id) return;

      const orderDoc = await getDoc(doc(db, 'orders', id));
      if (!orderDoc.exists()) {
        setError('Pedido não encontrado');
        return;
      }

      const data = orderDoc.data();
      
      // Buscar nome do vendedor
      let sellerName = '';
      try {
        const sellerDoc = await getDoc(doc(db, 'users', data.sellerId));
        if (sellerDoc.exists()) {
          const companyDoc = await getDoc(doc(db, 'companies', sellerDoc.data().companyId));
          if (companyDoc.exists()) {
            sellerName = companyDoc.data().commercialName || companyDoc.data().legalName;
          }
        }
      } catch (err) {
        console.error('Erro ao buscar nome do vendedor:', err);
      }

      setOrder({
        ...data,
        id: orderDoc.id,
        sellerName: sellerName || 'Empresa não encontrada'
      } as OrderDetails);
    } catch (err) {
      console.error('Erro ao carregar detalhes do pedido:', err);
      setError('Erro ao carregar detalhes do pedido. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || !currentUser || !order) return;

    setSubmittingComment(true);
    try {
      const orderRef = doc(db, 'orders', id!);
      const commentId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      await updateDoc(orderRef, {
        comments: arrayUnion({
          id: commentId,
          text: newComment.trim(),
          createdAt: new Date(),
          createdBy: currentUser.displayName || currentUser.email || 'Utilizador',
          userId: currentUser.uid
        })
      });

      // Atualizar o estado local
      setOrder(prev => {
        if (!prev) return null;
        return {
          ...prev,
          comments: [
            ...prev.comments,
            {
              id: commentId,
              text: newComment.trim(),
              createdAt: new Date(),
              createdBy: currentUser.displayName || currentUser.email || 'Utilizador'
            }
          ]
        };
      });

      setNewComment('');
    } catch (err) {
      console.error('Erro ao adicionar comentário:', err);
      alert('Erro ao adicionar comentário. Por favor, tente novamente.');
    } finally {
      setSubmittingComment(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="text-center py-12">
            <p className="text-red-500">{error || 'Pedido não encontrado'}</p>
            <button
              onClick={() => navigate('/dashboard/drop/orders')}
              className="mt-4 text-primary hover:text-primary-dark"
            >
              Voltar para Lista de Pedidos
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="bg-white rounded-lg shadow-md">
        <div className="p-6">
          <div className="flex items-center gap-4 mb-6">
            <button
              onClick={() => navigate('/dashboard/drop/orders')}
              className="inline-flex items-center gap-2 text-primary hover:text-primary-dark"
            >
              <ArrowLeft size={20} />
              Voltar para Lista de Pedidos
            </button>
            <h2 className="text-2xl font-bold text-secondary">
              Detalhes do Pedido #{order.orderNumber}
            </h2>
          </div>

          {/* Status do Pedido */}
          <div className="mb-8">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
              order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
              order.status === 'processing' ? 'bg-blue-100 text-blue-800' :
              order.status === 'completed' ? 'bg-green-100 text-green-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {order.status === 'pending' ? 'Pendente' :
               order.status === 'processing' ? 'Em Processamento' :
               order.status === 'completed' ? 'Concluído' :
               'Desconhecido'}
            </span>
          </div>

          {/* Informações do Vendedor */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-2">Informações do Vendedor</h3>
            <p className="text-gray-600">{order.sellerName}</p>
          </div>

          {/* Lista de Produtos */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-4">Produtos</h3>
            <div className="space-y-4">
              {order.items.map((item, index) => (
                <div key={index} className="flex items-center gap-4 border-b pb-4">
                  <div className="flex-shrink-0 w-16 h-16 bg-gray-200 rounded-md overflow-hidden">
                    {item.mainImage ? (
                      <img
                        src={item.mainImage}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package size={24} className="text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">{item.name}</p>
                    <p className="text-sm text-gray-500">
                      Quantidade: {item.quantity}
                    </p>
                    <p className="text-sm text-gray-500">
                      Preço unitário: {item.price.toLocaleString('pt-PT', {
                        style: 'currency',
                        currency: 'EUR'
                      })}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">
                      {item.total.toLocaleString('pt-PT', {
                        style: 'currency',
                        currency: 'EUR'
                      })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Endereço de Entrega */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-2">Endereço de Entrega</h3>
            <div className="bg-gray-50 p-4 rounded-md">
              <p className="font-medium">{order.shippingAddress.name}</p>
              <p>{order.shippingAddress.address}</p>
              <p>
                {order.shippingAddress.postalCode} {order.shippingAddress.city}
              </p>
              <p>{order.shippingAddress.district}</p>
              <p>{order.shippingAddress.country}</p>
              <p className="mt-2 text-sm text-gray-500">
                Email: {order.shippingAddress.email}
              </p>
              <p className="text-sm text-gray-500">
                Telefone: {order.shippingAddress.phone}
              </p>
            </div>
          </div>

          {/* Resumo do Pedido */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold mb-4">Resumo do Pedido</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Subtotal</span>
                <span>{order.subtotal.toLocaleString('pt-PT', {
                  style: 'currency',
                  currency: 'EUR'
                })}</span>
              </div>
              <div className="flex justify-between">
                <span>Portes de envio</span>
                <span>{order.shippingCost.toLocaleString('pt-PT', {
                  style: 'currency',
                  currency: 'EUR'
                })}</span>
              </div>
              <div className="flex justify-between font-bold text-lg pt-2 border-t">
                <span>Total</span>
                <span>{order.total.toLocaleString('pt-PT', {
                  style: 'currency',
                  currency: 'EUR'
                })}</span>
              </div>
            </div>
          </div>

          {/* Comentários */}
          <div className="mt-8 border-t pt-6">
            <h3 className="text-lg font-semibold mb-4">Comentários</h3>
            
            {/* Formulário de novo comentário */}
            <form onSubmit={handleAddComment} className="mb-6">
              <div className="flex flex-col space-y-2">
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Adicione um comentário..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                  rows={3}
                />
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={!newComment.trim() || submittingComment}
                    className={`px-4 py-2 text-white rounded-md ${
                      !newComment.trim() || submittingComment
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-primary hover:bg-primary-dark'
                    }`}
                  >
                    {submittingComment ? 'A enviar...' : 'Adicionar Comentário'}
                  </button>
                </div>
              </div>
            </form>

            {/* Lista de comentários */}
            <div className="space-y-4">
              {order.comments && order.comments.length > 0 ? (
                order.comments.map((comment) => (
                  <div key={comment.id} className="bg-gray-50 p-4 rounded-md">
                    <div className="flex justify-between items-start">
                      <p className="text-sm text-gray-600">{comment.text}</p>
                      {comment.userId === currentUser?.uid && (
                        <button
                          onClick={async () => {
                            if (!window.confirm('Tem certeza que deseja apagar este comentário?')) return;
                            try {
                              const orderRef = doc(db, 'orders', id!);
                              await updateDoc(orderRef, {
                                comments: arrayRemove(comment)
                              });
                              // Atualizar estado local
                              setOrder(prev => {
                                if (!prev) return null;
                                return {
                                  ...prev,
                                  comments: prev.comments.filter(c => c.id !== comment.id)
                                };
                              });
                            } catch (err) {
                              console.error('Erro ao apagar comentário:', err);
                              alert('Erro ao apagar comentário. Por favor, tente novamente.');
                            }
                          }}
                          className="text-sm text-red-500 hover:text-red-700"
                        >
                          Apagar
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {comment.createdBy} - {typeof comment.createdAt === 'string' 
                        ? new Date(comment.createdAt).toLocaleDateString('pt-PT', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        : comment.createdAt instanceof Date 
                          ? comment.createdAt.toLocaleDateString('pt-PT', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })
                          : comment.createdAt?.toDate?.()?.toLocaleDateString('pt-PT', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            }) || 'Data desconhecida'
                      }
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500 text-center">
                  Ainda não há comentários neste pedido.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
